import { useState } from 'react';
import { useParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Member, MemberBulkResult, page } from '@cloudsign/shared';
import { Lock } from 'lucide-react';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { ExportButton } from '../../components/ExportButton';
import { ErrorPanel, errorMessage } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  CheckboxField,
  ConfirmButton,
  EmptyRow,
  FilterBar,
  LinkButton,
  PageHeader,
  Pager,
  SelectField,
  SortHeader,
  Spinner,
  TableWrap,
  TextField,
  Th,
} from '../../components/ui';
import { MemberDrawer } from './MemberDrawer';
import { useGroupFields } from './hooks';

const MemberPage = page(Member);
const PAGE_SIZE = 25;

type StatusFilter = 'current' | 'archived' | 'all';
type ColumnKey = 'email' | 'phone' | 'role' | 'details' | 'status';
const COLUMNS: ColumnKey[] = ['email', 'phone', 'role', 'details', 'status'];

export function MembersPage() {
  const { id: groupId = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const canManage = useCan('groups.manage_members');
  const canDelete = useCan('groups.delete_members');
  const canExport = useCan('groups.export_members');
  const canSensitive = useCan('groups.manage_sensitive');

  const list = useListState({ sort: 'fullName' });
  const [status, setStatus] = useState<StatusFilter>('current');
  const [shown, setShown] = useState<Record<ColumnKey, boolean>>({ email: true, phone: false, role: true, details: true, status: true });
  const [selected, setSelected] = useState<string[]>([]);
  const [drawer, setDrawer] = useState<'new' | string | null>(null);
  const [bulkField, setBulkField] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkNote, setBulkNote] = useState<string | null>(null);

  const query = { groupId, status, search: list.search, sort: list.sort, order: list.order, page: list.page, pageSize: PAGE_SIZE };
  const q = useQuery({
    queryKey: ['members', 'list', groupId, query],
    queryFn: () => api.get('/members', { query, schema: MemberPage }),
    enabled: groupId !== '',
    placeholderData: keepPreviousData,
  });
  const fields = useGroupFields(groupId);
  const bulkFields = (fields.data ?? []).filter((f) => f.status === 'active' && (canSensitive || !f.sensitive));

  const bulk = useMutation({
    mutationFn: (action: Record<string, unknown>) =>
      api.post('/members/bulk', { body: { groupId, memberIds: selected, action }, schema: MemberBulkResult }),
    onSuccess: (r) => {
      setBulkNote(t('membersUi.bulk.result', { updated: r.updated, skipped: r.skipped }));
      toast(t('membersUi.bulk.result', { updated: r.updated, skipped: r.skipped }));
      setSelected([]);
      void qc.invalidateQueries({ queryKey: ['members', 'list'] });
    },
  });

  const items = q.data?.items ?? [];
  const allOnPage = items.length > 0 && items.every((m) => selected.includes(m.id));
  const colCount = 1 + (canManage ? 1 : 0) + COLUMNS.filter((c) => shown[c]).length;
  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const sortProps = { sort: list.sort, order: list.order, onSort: list.toggleSort };
  const label = (c: ColumnKey) => t(`membersUi.col.${c}`);

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6">
      <PageHeader
        title={t('membersUi.title')}
        description={t('membersUi.description')}
        actions={
          <>
            {canExport ? <ExportButton path="/members/export" query={{ groupId, status }} fileName="members" /> : null}
            {canManage ? (
              <LinkButton to={`/manage/groups/${groupId}/members/import`}>{t('membersUi.import')}</LinkButton>
            ) : null}
            {canManage ? (
              <Button variant="primary" onClick={() => setDrawer('new')}>
                {t('membersUi.add')}
              </Button>
            ) : null}
          </>
        }
      />

      <FilterBar columns="sm:grid-cols-2">
        <TextField
          label={t('membersUi.search')}
          type="search"
          value={list.search}
          onChange={(e) => list.setSearch(e.target.value)}
        />
        <SelectField
          label={t('membersUi.statusFilter')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as StatusFilter);
            setSelected([]);
            list.resetPage();
          }}
        >
          {(['current', 'archived', 'all'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`membersUi.status.${s}`)}
            </option>
          ))}
        </SelectField>
      </FilterBar>

      <fieldset className="flex flex-wrap items-center gap-x-5 gap-y-1">
        <legend className="sr-only">{t('membersUi.columns')}</legend>
        <span className="text-sm font-medium text-slate-700">{t('membersUi.columns')}</span>
        {COLUMNS.map((c) => (
          <CheckboxField key={c} label={label(c)} checked={shown[c]} onChange={(e) => setShown((s) => ({ ...s, [c]: e.target.checked }))} />
        ))}
      </fieldset>

      {canManage && selected.length > 0 ? (
        <div role="region" aria-label={t('membersUi.selected', { count: selected.length })} className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-medium">{t('membersUi.selected', { count: selected.length })}</p>
          <div className="flex flex-wrap items-center gap-2">
            <Button disabled={bulk.isPending} onClick={() => bulk.mutate({ type: 'archive' })}>
              {t('membersUi.bulk.archive')}
            </Button>
            <Button disabled={bulk.isPending} onClick={() => bulk.mutate({ type: 'restore' })}>
              {t('membersUi.bulk.restore')}
            </Button>
            {canDelete ? (
              <ConfirmButton label={t('membersUi.bulk.delete')} pending={bulk.isPending} onConfirm={() => bulk.mutate({ type: 'delete' })} />
            ) : null}
          </div>
          {bulkFields.length > 0 ? (
            <div className="flex flex-wrap items-end gap-2">
              <SelectField label={t('membersUi.bulk.field')} value={bulkField} onChange={(e) => setBulkField(e.target.value)}>
                <option value="" />
                {bulkFields.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </SelectField>
              <TextField label={t('membersUi.bulk.value')} value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} />
              <Button
                disabled={bulk.isPending || bulkField === ''}
                onClick={() => bulk.mutate({ type: 'set_field', fieldId: bulkField, value: bulkValue })}
              >
                {t('membersUi.bulk.apply')}
              </Button>
            </div>
          ) : null}
          {bulk.error ? <Alert>{errorMessage(bulk.error)}</Alert> : null}
        </div>
      ) : null}
      {bulkNote && selected.length === 0 ? <Alert kind="success">{bulkNote}</Alert> : null}

      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('membersUi.table')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                {canManage ? (
                  <Th>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      aria-label={t('membersUi.selectAll')}
                      checked={allOnPage}
                      onChange={() => setSelected(allOnPage ? [] : items.map((m) => m.id))}
                    />
                  </Th>
                ) : null}
                <SortHeader label={t('membersUi.col.name')} field="fullName" {...sortProps} />
                {shown.email ? <SortHeader label={label('email')} field="email" {...sortProps} /> : null}
                {shown.phone ? <Th>{label('phone')}</Th> : null}
                {shown.role ? <SortHeader label={label('role')} field="role" {...sortProps} /> : null}
                {shown.details ? <Th>{label('details')}</Th> : null}
                {shown.status ? <Th>{label('status')}</Th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 ? (
                <EmptyRow cols={colCount} />
              ) : (
                items.map((m) => (
                  <tr key={m.id}>
                    {canManage ? (
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          aria-label={t('membersUi.selectOne', { name: m.fullName })}
                          checked={selected.includes(m.id)}
                          onChange={() => toggle(m.id)}
                        />
                      </td>
                    ) : null}
                    <td className="px-4 py-2">
                      {canManage ? (
                        <button type="button" className="flex items-center gap-2 text-left font-medium text-brand hover:underline" onClick={() => setDrawer(m.id)}>
                          <Avatar name={m.fullName} />
                          {m.fullName}
                        </button>
                      ) : (
                        <span className="flex items-center gap-2 font-medium">
                          <Avatar name={m.fullName} />
                          {m.fullName}
                        </span>
                      )}
                    </td>
                    {shown.email ? <td className="px-4 py-2">{m.email ?? ''}</td> : null}
                    {shown.phone ? <td className="px-4 py-2">{m.phone ?? ''}</td> : null}
                    {shown.role ? <td className="px-4 py-2">{m.role ?? ''}</td> : null}
                    {shown.details ? (
                      <td className="px-4 py-2 text-slate-600">
                        {m.fields.map((f) =>
                          f.redacted ? (
                            <Lock key={f.fieldId} aria-label={t('membersUi.sensitive')} className="mr-1 inline h-3.5 w-3.5" />
                          ) : f.text ? (
                            <span key={f.fieldId} className="mr-3 inline-block">
                              {f.label}: {f.text}
                            </span>
                          ) : null,
                        )}
                      </td>
                    ) : null}
                    {shown.status ? (
                      <td className="px-4 py-2">
                        <Badge tone={m.status === 'current' ? 'good' : 'neutral'}>
                          {m.status === 'current' ? t('membersUi.currentBadge') : t('membersUi.archivedBadge')}
                        </Badge>
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </TableWrap>
          <Pager page={list.page} pageSize={PAGE_SIZE} total={q.data.total} onPage={list.setPage} />
        </>
      )}

      {drawer ? <MemberDrawer groupId={groupId} memberId={drawer === 'new' ? null : drawer} onClose={() => setDrawer(null)} /> : null}
    </div>
  );
}
